/**
 * Typography scale and MUI variant definitions.
 * All font sizes defined here — no arbitrary rem in theme.
 */

/** Font size scale (px). Base 16px. */
export const fontSize = {
  /** 29px - h1 */
  h1: 29,
  /** 20px - h2 */
  h2: 20,
  /** 18px - h3 */
  h3: 18,
  /** 16px - h4, body base */
  h4: 16,
  /** 15px - h5, body1 */
  h5: 15,
  /** 14px - h6, body2 */
  h6: 14,
  /** 12px - caption */
  caption: 12,
} as const;

/** Font family stack. */
export const fontFamily = "'Plus Jakarta Sans', 'Inter', sans-serif";

/** Line height scale. */
export const lineHeight = {
  tight: 1.25,
  snug: 1.3,
  normal: 1.35,
  relaxed: 1.4,
  loose: 1.45,
  body: 1.5,
} as const;

/** Font weights. */
export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** Helper: px to rem (16px base). */
function pxToRem(px: number): string {
  return `${px / 16}rem`;
}

/**
 * MUI typography variant config.
 * Consumed by createAppTheme.
 */
export function getTypographyVariants() {
  return {
    fontFamily,
    h1: {
      fontFamily,
      fontSize: pxToRem(fontSize.h1),
      fontWeight: fontWeight.bold,
      lineHeight: lineHeight.tight,
    },
    h2: {
      fontFamily,
      fontSize: pxToRem(fontSize.h2),
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.snug,
    },
    h3: {
      fontFamily,
      fontSize: pxToRem(fontSize.h3),
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.normal,
    },
    h4: {
      fontFamily,
      fontSize: pxToRem(fontSize.h4),
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.relaxed,
    },
    h5: {
      fontFamily,
      fontSize: pxToRem(fontSize.h5),
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.relaxed,
    },
    h6: {
      fontFamily,
      fontSize: pxToRem(fontSize.h6),
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.loose,
    },
    subtitle1: {
      fontFamily,
      fontSize: pxToRem(fontSize.h5),
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.body,
    },
    subtitle2: {
      fontFamily,
      fontSize: pxToRem(fontSize.h6),
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.body,
    },
    body1: {
      fontFamily,
      fontSize: pxToRem(fontSize.h5),
      lineHeight: lineHeight.body,
    },
    body2: {
      fontFamily,
      fontSize: pxToRem(fontSize.h6),
      lineHeight: lineHeight.body,
    },
    caption: {
      fontFamily,
      fontSize: pxToRem(fontSize.caption),
      lineHeight: lineHeight.body,
    },
    button: {
      fontFamily,
      fontWeight: fontWeight.semibold,
    },
  };
}
